"use client";
import {useEffect, useRef, type MouseEventHandler} from 'react';
import {ArrowLeft, ChevronRight} from 'lucide-react';
import {AtlasIcon} from './AtlasControl';

type Props = {
 returning:boolean;
 hideNextTime:boolean;
 onHideNextTimeChange:(hide:boolean)=>void;
 onContinue:()=>void;
 onAtlas:()=>void;
 onHomeNavigate?:MouseEventHandler<HTMLAnchorElement>;
};

export function ProfessionalProfileIntroduction({returning,hideNextTime,onHideNextTimeChange,onContinue,onAtlas,onHomeNavigate}:Props){
 const heading=useRef<HTMLHeadingElement>(null);
 useEffect(()=>{heading.current?.focus({preventScroll:true});},[]);
 return <main className="pc-introduction" aria-labelledby="pc-introduction-title">
  <div className="pc-intro-scroll" tabIndex={0} role="region" aria-label="About your professional profile">
   <div className="pc-intro-content">
    <a className="pc-back" href="/" onClick={onHomeNavigate}><ArrowLeft size={16} aria-hidden="true"/> Participant home</a>
    <header className="pc-intro-heading">
     <h1 id="pc-introduction-title" ref={heading} tabIndex={-1}>Your professional profile</h1>
     <p>To prepare your immigration case, GM needs an accurate record of your professional experience and evidence supporting it. Your profile brings together your work history, achievements, and supporting documents.</p>
    </header>

    <section className="pc-intro-contents" aria-labelledby="pc-intro-contents-title">
     <h2 id="pc-intro-contents-title">What it may include</h2>
     <dl>
      <div><dt>Professional introduction</dt><dd>A summary of your experience, significant work, and achievements, prepared from the information you provide.</dd></div>
      <div><dt>Work history</dt><dd>Productions, roles, dates, and responsibilities.</dd></div>
      <div><dt>Records of your work</dt><dd>Credits, production materials, and relevant contracts.</dd></div>
      <div><dt>Achievements and recognition</dt><dd>Awards, nominations, articles, reviews, and records of a production’s success.</dd></div>
      <div><dt>Other evidence</dt><dd>Professional letters, compensation records, or documents GM identifies as relevant.</dd></div>
     </dl>
     <p className="pc-intro-reassurance">You won’t need every type of document listed here or supporting paperwork for every job. GM will help identify what matters for your case.</p>
    </section>

    <div className="pc-intro-guidance">
     <section aria-labelledby="pc-intro-start-title">
      <h2 id="pc-intro-start-title">Where to start</h2>
      <p>Start with online or social media profiles, a resume, or your own notes. Add a few details or your whole background. You can combine these methods and add documents in any order.</p>
      <p>We’ll use public information from the accounts and links you share to help prepare your professional profile for immigration review.</p>
     </section>
     <section className="pc-intro-atlas" aria-labelledby="pc-intro-atlas-title">
      <h2 id="pc-intro-atlas-title"><AtlasIcon/> Guided help with Atlas</h2>
      <p>Atlas can walk you through this interactively, one question at a time. It can help you describe your experience and understand which supporting documents may be useful.</p>
     </section>
    </div>

    <section className="pc-intro-gm" aria-labelledby="pc-intro-gm-title">
     <h2 id="pc-intro-gm-title">What GM does</h2>
     <p>We prepare a professional introduction and organize your career history for you to check. We review the supporting evidence and follow up on anything missing.</p>
    </section>
   </div>
  </div>
  <div className="pc-intro-footer">
   <div className="pc-intro-footer-inner">
    <label className="pc-intro-preference"><input type="checkbox" checked={hideNextTime} onChange={e=>onHideNextTimeChange(e.target.checked)}/><span>Don’t show this introduction again</span></label>
    <div className="pc-intro-actions">
     <button className="pc-primary" onClick={onContinue}>{returning?'Continue my profile':'Start my profile'}<ChevronRight size={17} aria-hidden="true"/></button>
     <button className="pc-link" onClick={onAtlas}>Talk with Atlas</button>
    </div>
   </div>
  </div>
 </main>;
}
